export default function registerMobileMenu(Alpine) {
  Alpine.data('mobileMenu', () => ({
    open: false,

    init() {
      this.syncBodyLock();
      this.setupSubmenuToggles();
      document.addEventListener('turbo:before-visit', () => this.close(), { passive: true });
    },

    toggle() {
      if (this.open) {
        this.close();
        return;
      }

      this.openMenu();
    },

    openMenu() {
      this.open = true;
      this.syncBodyLock();
    },

    close() {
      this.open = false;
      this.syncBodyLock();
    },

    syncBodyLock() {
      document.body.classList.toggle('overflow-hidden', this.open);
    },

    setupSubmenuToggles() {
      const menu = this.$root.querySelector('.mobile-menu-nav');

      if (!menu) {
        return;
      }

      menu.classList.add('list-none', 'm-0', 'p-0');

      const topLevelItems = Array.from(menu.children).filter(
        (child) => child instanceof HTMLElement && child.classList.contains('menu-item')
      );

      topLevelItems.forEach((menuItem) => {
        menuItem.classList.add('mobile-menu-item');

        if (menuItem.classList.contains('menu-item-has-children')) {
          menuItem.classList.add('mobile-menu-item--parent');
        }

        const itemLink = Array.from(menuItem.children).find(
          (child) => child instanceof HTMLAnchorElement
        );
        if (itemLink) {
          itemLink.classList.add(
            'inline-flex',
            'w-fit',
            'py-[0.82rem]',
            'text-[0.9rem]',
            'text-white/90',
            'uppercase',
            'tracking-[0.09em]',
            'font-semibold'
          );
        }
      });

      menu.querySelectorAll('.sub-menu').forEach((submenu) => {
        submenu.classList.add('grid', 'gap-0', 'list-none', 'm-0', 'p-0', 'pt-[0.7rem]', 'grid-cols-1');

        Array.from(submenu.children).forEach((submenuItem) => {
          if (!(submenuItem instanceof HTMLElement)) {
            return;
          }

          const itemLink = Array.from(submenuItem.children).find(
            (child) => child instanceof HTMLAnchorElement
          );

          if (!itemLink) {
            return;
          }

          itemLink.classList.add(
            'block',
            'py-[0.55rem]',
            'pl-[0.95rem]',
            'text-[0.86rem]',
            'text-white/72',
            'tracking-[0.05em]',
            'uppercase',
            'font-semibold'
          );
        });
      });

      const menuToggleBaseClass = 'w-8 h-8 text-base leading-none border-0 bg-transparent flex items-center justify-center transition-colors duration-200';

      const menuItems = menu.querySelectorAll('.menu-item-has-children');
      let hasInitialOpen = false;

      const syncExpandedControls = (toggleButton, touchTarget, expanded) => {
        if (toggleButton) {
          toggleButton.textContent = expanded ? '-' : '+';
          toggleButton.classList.toggle('text-red', expanded);
          toggleButton.classList.toggle('text-white/74', !expanded);
          toggleButton.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        }

        if (touchTarget) {
          touchTarget.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        }
      };

      const closeOtherSubmenus = (activeItem) => {
        menuItems.forEach((otherItem) => {
          if (!(otherItem instanceof HTMLElement) || otherItem === activeItem) {
            return;
          }

          const otherChildren = Array.from(otherItem.children);
          const otherSubmenu = otherChildren.find(
            (child) => child instanceof HTMLElement && child.classList.contains('sub-menu')
          );
          const otherTouchTarget = otherChildren.find(
            (child) => child instanceof HTMLElement && child.classList.contains('mobile-submenu-touch-target')
          );
          const otherToggleButton = otherChildren.find(
            (child) => child instanceof HTMLElement && child.classList.contains('mobile-submenu-toggle')
          );

          if (!otherSubmenu) {
            return;
          }

          if (!otherItem.classList.contains('is-open')) {
            return;
          }

          if (otherSubmenu.style.maxHeight === 'none') {
            otherSubmenu.style.maxHeight = `${otherSubmenu.scrollHeight}px`;
          } else {
            otherSubmenu.style.maxHeight = `${otherSubmenu.scrollHeight}px`;
          }

          otherSubmenu.setAttribute('aria-hidden', 'true');
          otherItem.classList.remove('is-open');

          syncExpandedControls(otherToggleButton, otherTouchTarget, false);

          requestAnimationFrame(() => {
            otherSubmenu.style.maxHeight = '0px';
          });
        });
      };

      if (menu.dataset.closeReady !== 'true') {
        menu.addEventListener('click', (event) => {
          const link = event.target.closest('a');

          if (!link) {
            return;
          }

          if (link.parentElement?.classList.contains('menu-item-has-children')) {
            return;
          }

          this.close();
        });

        menu.dataset.closeReady = 'true';
      }

      menuItems.forEach((item) => {
        if (item.dataset.submenuReady === 'true') {
          return;
        }

        const directChildren = Array.from(item.children);
        const link = directChildren.find((child) => child instanceof HTMLAnchorElement);
        const submenu = directChildren.find(
          (child) => child instanceof HTMLElement && child.classList.contains('sub-menu')
        );

        if (!link || !submenu) {
          return;
        }

        const shouldStartExpanded = item.classList.contains('current-menu-ancestor') || item.classList.contains('current-menu-item');
        const startsExpanded = !hasInitialOpen && shouldStartExpanded;
        if (startsExpanded) {
          hasInitialOpen = true;
        }
        submenu.classList.add('mobile-submenu-panel');
        submenu.setAttribute('aria-hidden', startsExpanded ? 'false' : 'true');
        item.classList.toggle('is-open', startsExpanded);

        const touchTarget = document.createElement('button');
        touchTarget.type = 'button';
        touchTarget.className = 'mobile-submenu-touch-target';
        touchTarget.setAttribute('aria-label', `Toggle ${link.textContent.trim()} submenu`);

        const button = document.createElement('button');
        button.type = 'button';
        button.className = `mobile-submenu-toggle ${menuToggleBaseClass}`;
        button.setAttribute('aria-label', `Toggle ${link.textContent.trim()} submenu`);
        syncExpandedControls(button, touchTarget, startsExpanded);
        submenu.style.maxHeight = startsExpanded ? `${submenu.scrollHeight}px` : '0px';

        submenu.addEventListener('transitionend', (event) => {
          if (event.propertyName !== 'max-height') {
            return;
          }

          if (item.classList.contains('is-open')) {
            submenu.style.maxHeight = 'none';
          }
        });

        const toggleSubmenu = () => {
          const willOpen = !item.classList.contains('is-open');

          if (willOpen) {
            closeOtherSubmenus(item);
            item.classList.add('is-open');
            submenu.setAttribute('aria-hidden', 'false');
            syncExpandedControls(button, touchTarget, true);

            if (submenu.style.maxHeight === 'none') {
              submenu.style.maxHeight = '0px';
            }

            requestAnimationFrame(() => {
              submenu.style.maxHeight = `${submenu.scrollHeight}px`;
            });

            return;
          }

          if (submenu.style.maxHeight === 'none') {
            submenu.style.maxHeight = `${submenu.scrollHeight}px`;
          } else {
            submenu.style.maxHeight = `${submenu.scrollHeight}px`;
          }

          submenu.setAttribute('aria-hidden', 'true');
          item.classList.remove('is-open');
          syncExpandedControls(button, touchTarget, false);

          requestAnimationFrame(() => {
            submenu.style.maxHeight = '0px';
          });
        };

        touchTarget.addEventListener('click', toggleSubmenu);
        button.addEventListener('click', toggleSubmenu);

        link.insertAdjacentElement('afterend', touchTarget);
        touchTarget.insertAdjacentElement('afterend', button);
        item.dataset.submenuReady = 'true';
      });
    },
  }));
}
